<div class="dashboard-container send-reading-page">
  <main class="dashboard-content" [class.has-sticky]="showForm()">
    @if (!submissionResult()) {
      <header class="page-header">
        <div class="page-header-top">
          <h2 class="title mb-0">Enviar lectura</h2>
        </div>
        <p class="page-subtitle">
          Registra la lectura de tus contadores convencionales cuando no puedas estar en el
          domicilio el día de la visita.
        </p>

        <p-message severity="warn" styleClass="disclaimer-message">
          <div class="disclaimer-content">
            <span>
              Envía la lectura en las 24 horas siguientes a la visita del lector para que se
              registre en la liquidación actual.
            </span>
            <button
              type="button"
              class="disclaimer-toggle"
              (click)="toggleHelp()"
              [attr.aria-expanded]="helpExpanded()"
            >
              {{ helpExpanded() ? 'Menos información' : 'Más información' }}
            </button>
          </div>
          @if (helpExpanded()) {
            <div class="disclaimer-extra">
              <p>
                Si eliges esta opción, no tienes que rellenar la tarjeta de lectura que hemos dejado
                en el domicilio. Si nos envías los datos más tarde, se registrarán en la siguiente
                liquidación.
              </p>
              <p>Al introducir el valor de la lectura no debes poner los litros.</p>
              <p class="mb-0">
                La foto del contador es recomendable para verificar la lectura, pero no es
                obligatoria salvo que la lectura sea inferior a la anterior.
              </p>
            </div>
          }
        </p-message>

        @if (!loaded()) {
          <section class="upcoming-panel card-panel" aria-busy="true">
            <p-skeleton width="9rem" height="0.95rem" styleClass="mb-3" />
            <p-skeleton width="70%" height="0.85rem" styleClass="mb-2" />
            <p-skeleton width="55%" height="0.85rem" />
          </section>
        } @else if (supplies().length > 0) {
          <section class="upcoming-panel card-panel" aria-label="Próximas lecturas">
            <h3 class="upcoming-title">Próximas lecturas</h3>
            <ul class="upcoming-list">
              @for (supply of supplies(); track supply.supplyTypeId ?? supply.id) {
                <li>
                  <strong>{{ supply.supplyName ?? supply.name }}</strong
                  >:
                  @if (supply.readingPlannedDate) {
                    {{ supply.readingPlannedDate | date: 'dd/MM/yyyy' }}
                  } @else {
                    <span class="text-secondary">Sin fecha programada</span>
                  }
                </li>
              }
            </ul>
          </section>
        }
      </header>
    }

    @if (!loaded() && !submissionResult()) {
      <section class="input-panel" aria-busy="true">
        <div class="panel-title">
          <p-skeleton shape="circle" size="18px" />
          <p-skeleton width="14rem" height="1.05rem" />
        </div>

        <article class="meter-card card-panel send-reading-meter-skeleton" aria-hidden="true">
          <div class="meter-card-header">
            <div class="meter-identity">
              <p-skeleton width="7rem" height="1.05rem" />
              <p-skeleton width="10rem" height="0.85rem" />
            </div>
          </div>

          <div class="last-reading-kpi send-reading-kpi-skeleton">
            <div class="kpi-label-row">
              <p-skeleton width="8rem" height="0.8rem" />
            </div>
            <div class="kpi-values">
              <div class="kpi-item">
                <p-skeleton width="2.5rem" height="0.8rem" />
                <p-skeleton width="6.5rem" height="1.35rem" />
              </div>
              <div class="kpi-item">
                <p-skeleton width="3rem" height="0.8rem" />
                <p-skeleton width="4.5rem" height="1.35rem" />
              </div>
            </div>
          </div>

          <div class="fields-row">
            @for (field of [1, 2, 3]; track field) {
              <div class="field">
                <p-skeleton width="55%" height="0.9rem" />
                <p-skeleton width="100%" height="2.5rem" borderRadius="6px" />
              </div>
            }
          </div>
        </article>

        <div class="form-actions" aria-hidden="true">
          <p-skeleton width="9rem" height="2.5rem" borderRadius="6px" />
          <p-skeleton width="11rem" height="2.5rem" borderRadius="6px" />
        </div>
      </section>
    }

    @if (loaded() && !hasMeters() && !submissionResult()) {
      <section class="empty-state" aria-live="polite">
        <div class="empty-icon">
          <i-lucide name="gauge" [size]="36" aria-hidden="true"></i-lucide>
        </div>
        <h3>Sin contadores convencionales</h3>
        <p>No hay contadores de lectura convencional disponibles en esta propiedad.</p>
        <p-button label="Volver al inicio" icon="pi pi-home" (onClick)="goHome()" />
      </section>
    }

    @if (showForm()) {
      <section class="input-panel">
        <div class="panel-title">
          <i-lucide name="gauge" [size]="18" aria-hidden="true"></i-lucide>
          <h3>Contadores de esta propiedad</h3>
        </div>

        <form [formGroup]="form" class="meters-form">
          <div formArrayName="meters" class="meters-list">
            @for (control of meters.controls; track control.get('meterId')?.value; let i = $index) {
              <article
                class="meter-card card-panel"
                [formGroupName]="i"
                [class.row-ready]="rowReady(i)"
                [class.row-equal]="alertReadingStatus(i) === 'equal'"
                [class.row-lower]="photoRequired(i)"
              >
                <div class="meter-card-header">
                  <div class="meter-identity">
                    <span class="meter-serial">{{ control.get('serialNumber')?.value }}</span>
                    <span class="meter-meta">
                      {{ control.get('supplyType')?.value }}
                      @if (control.get('location')?.value) {
                        <span aria-hidden="true">·</span>
                        {{ control.get('location')?.value }}
                      }
                    </span>
                  </div>
                  @if (rowReady(i)) {
                    <span class="ready-chip">
                      <i-lucide name="check" [size]="14" aria-hidden="true"></i-lucide>
                      Lista
                    </span>
                  }
                </div>

                <div class="last-reading-kpi" aria-label="Última lectura">
                  <div class="kpi-label-row">
                    <span class="kpi-eyebrow">Última lectura</span>
                  </div>
                  <div class="kpi-values">
                    <div class="kpi-item">
                      <span class="label">Fecha</span>
                      <span class="value">
                        @if (control.get('lastReadingDate')?.value) {
                          {{ control.get('lastReadingDate')?.value | date: 'dd/MM/yyyy' }}
                        } @else {
                          —
                        }
                      </span>
                    </div>
                    <div class="kpi-item">
                      <span class="label">Lectura</span>
                      <span class="value highlight">
                        @if (displayedLastReading(control); as lastShown) {
                          {{ lastShown | number: '1.0-2' }}
                          <span class="unit">{{ unitsFor(i) }}</span>
                        } @else {
                          —
                        }
                      </span>
                    </div>
                  </div>
                </div>

                <div class="fields-row">
                  <div class="field">
                    <label [attr.for]="'reading-' + i">
                      Lectura actual
                      <span class="unit-inline">({{ unitsFor(i) }})</span>
                    </label>
                    <p-inputNumber
                      [inputId]="'reading-' + i"
                      formControlName="value"
                      [min]="0"
                      placeholder="0,00"
                      [fluid]="true"
                    />
                  </div>

                  <div class="field consumption-field">
                    <span class="label-static">
                      Consumo
                      <span class="unit-inline">({{ unitsFor(i) }})</span>
                    </span>
                    <div
                      class="consumption-live"
                      [class.has-value]="consumptionFor(i) != null"
                      [class.is-negative]="(consumptionFor(i) ?? 0) < 0"
                      aria-live="polite"
                    >
                      @if (consumptionFor(i) !== null) {
                        <span class="consumption-number">
                          {{ consumptionFor(i) | number: '1.2-2' }}
                          <span class="unit">{{ unitsFor(i) }}</span>
                        </span>
                      } @else {
                        <span class="consumption-placeholder">—</span>
                      }
                    </div>
                  </div>

                  <div class="field photo-field" [class.photo-required]="photoRequired(i)">
                    <span class="label-static">
                      Foto
                      @if (photoRequired(i)) {
                        <span class="required-hint">(obligatoria)</span>
                      } @else {
                        <span class="optional-hint">(recomendada)</span>
                      }
                    </span>
                    <div class="photo-row">
                      <p-fileUpload
                        #fileUpload
                        mode="basic"
                        chooseIcon="pi pi-camera"
                        chooseLabel="Adjuntar"
                        (onSelect)="onFileSelect($event, i)"
                        accept=".jpg,.jpeg,.png,.pdf"
                        [auto]="false"
                      />

                      @if (control.get('attachment')?.value; as file) {
                        <div class="file-info">
                          @if (previewUrl(+control.get('meterId')?.value); as url) {
                            <img [src]="url" alt="" class="file-thumb" />
                          }
                          <span class="file-name" [title]="file.name">{{ file.name }}</span>
                          <p-button
                            icon="pi pi-times"
                            [rounded]="true"
                            [text]="true"
                            severity="danger"
                            size="small"
                            ariaLabel="Quitar archivo"
                            (onClick)="clearAttachment(i, fileUpload)"
                          />
                        </div>
                      }
                    </div>
                  </div>
                </div>

                @if (alertReadingStatus(i) === 'equal') {
                  <p-message severity="warn" styleClass="reading-alert" aria-live="polite">
                    La lectura coincide con la anterior. Verifica que no se haya registrado consumo
                    y, si puedes, adjunta una foto del contador.
                  </p-message>
                }

                @if (alertReadingStatus(i) === 'lower' && photoRequired(i)) {
                  <p-message
                    severity="error"
                    styleClass="reading-alert reading-alert-lower"
                    aria-live="assertive"
                  >
                    La lectura es inferior a la anterior. Para enviarla debes adjuntar una foto que
                    verifique el valor del contador.
                  </p-message>
                }

                @if (alertReadingStatus(i) === 'lower' && !photoRequired(i)) {
                  <p-message severity="warn" styleClass="reading-alert" aria-live="polite">
                    La lectura es inferior a la anterior (consumo negativo). Revisa que el valor y
                    la foto del contador sean correctos.
                  </p-message>
                }
              </article>
            }
          </div>

          <div class="form-actions">
            <app-send-reading-help-dialog />
            <p-button
              class="submit-btn"
              label="Enviar lecturas"
              icon="pi pi-send"
              (onClick)="save()"
              [loading]="isSubmitting()"
              [disabled]="!canSubmit()"
            />
          </div>
        </form>
      </section>

      <div class="mobile-sticky-bar" aria-label="Acciones de envío de lectura">
        <div class="sticky-total">
          <span class="label">Listas</span>
          <span class="value">{{ readyCount() }}</span>
        </div>
        <p-button
          label="Enviar"
          icon="pi pi-send"
          (onClick)="save()"
          [loading]="isSubmitting()"
          [disabled]="!canSubmit()"
          size="small"
        />
      </div>
    }

    @if (submissionResult(); as result) {
      <section class="result-section reveal" aria-live="polite">
        <div class="result-hero card-panel">
          <div class="hero-top">
            <div class="thanks-icon" aria-hidden="true">
              <i-lucide name="check-circle" [size]="28"></i-lucide>
            </div>
            <h2 #resultHeading class="hero-title" tabindex="-1">
              Gracias por registrar las lecturas
            </h2>
          </div>

          <p class="hero-subtitle">
            @if (result.failed.length === 0) {
              Se han registrado correctamente {{ result.succeeded.length }} lectura(s).
            } @else {
              Se han registrado {{ result.succeeded.length }} lectura(s).
              {{ result.failed.length }} no se pudieron enviar.
            }
          </p>

          <div class="hero-actions">
            <p-button label="Volver al inicio" icon="pi pi-home" (onClick)="goHome()" />
          </div>
        </div>

        <div class="detail-panel card-panel">
          <h3 class="detail-heading">Resumen de lo enviado</h3>

          <div class="summary-list">
            @for (item of result.succeeded; track item.meterId) {
              <div class="summary-block success">
                <div class="s-row">
                  <span class="s-label">Contador</span>
                  <span class="s-value">{{ item.serialNumber }}</span>
                </div>
                <div class="s-row">
                  <span class="s-label">Suministro</span>
                  <span class="s-value">{{ item.supplyType }}</span>
                </div>
                @if (item.location) {
                  <div class="s-row">
                    <span class="s-label">Localización</span>
                    <span class="s-value">{{ item.location }}</span>
                  </div>
                }
                <div class="s-row">
                  <span class="s-label">Lectura enviada</span>
                  <span class="s-value">{{ item.value | number: '1.0-2' }} {{ item.units }}</span>
                </div>
                <div class="s-row">
                  <span class="s-label">Consumo</span>
                  <span class="s-value"
                    >{{ item.consumption | number: '1.2-2' }} {{ item.units }}</span
                  >
                </div>
                <div class="s-row total">
                  <span class="s-label">Foto</span>
                  <span class="s-value">{{ item.hasPhoto ? 'Sí' : 'No' }}</span>
                </div>
              </div>
            }

            @for (item of result.failed; track item.meterId) {
              <div class="summary-block failed">
                <div class="s-row">
                  <span class="s-label">Contador</span>
                  <span class="s-value">{{ item.serialNumber }}</span>
                </div>
                <div class="s-row">
                  <span class="s-label">Suministro</span>
                  <span class="s-value">{{ item.supplyType }}</span>
                </div>
                <div class="s-row">
                  <span class="s-label">Lectura</span>
                  <span class="s-value">{{ item.value | number: '1.0-2' }} {{ item.units }}</span>
                </div>
                <div class="s-row total">
                  <span class="s-label">Estado</span>
                  <span class="s-value">No enviada</span>
                </div>
              </div>
            }
          </div>
        </div>
      </section>
    }
  </main>
</div>
